import type {
  ApplicationContent,
  ApplicationPlan,
  ApplicationPreview,
  CreatedEntry,
  LibraryApplication,
  LibraryImportResult,
  LibraryVessel,
  PmsLibrary,
  SubmitOutcome,
} from './types.js';

/**
 * Şirket PMS kütüphanesinin veri kaynağı (web ofis; ADR 0063). Yazma işlemleri yetkiye göre
 * sunucuda denetlenir; düğmeler `can` bilgisinden değil hata kodundan da geri bildirim verir.
 */
export interface PmsLibrarySource {
  load(): Promise<PmsLibrary>;
  vessels(): Promise<LibraryVessel[]>;
  /** Excel içe aktarma: `preview` hiçbir şey yazmaz. */
  importFile(file: Blob, preview: boolean): Promise<LibraryImportResult>;
  /** Ortağın kütüphanesi şablon olarak (aynı içe aktarma yolu). */
  importTemplate(preview: boolean): Promise<LibraryImportResult>;
  /** Kütüphanenin Excel'i (ortağın biçimi). */
  exportFile(): Promise<void>;
  updateItem(id: string, body: Record<string, unknown>): Promise<void>;
  updateJob(id: string, body: Record<string, unknown>): Promise<void>;
  /** Elle ekleme (ADR 0102): şirketin sistemi, sisteme kalem, kaleme iş. */
  createSystem(body: Record<string, unknown>): Promise<CreatedEntry>;
  createItem(body: Record<string, unknown>): Promise<CreatedEntry>;
  createJob(body: Record<string, unknown>): Promise<CreatedEntry>;
  removeItem(id: string, rowVersion: number): Promise<void>;
  removeJob(id: string, rowVersion: number): Promise<void>;
  previewApplication(vesselId: string, content: ApplicationContent): Promise<ApplicationPreview>;
  /** Hatalı planda hata `{ code: 'APPLICATION_HAS_ERRORS', plan }` taşır. */
  submitApplication(
    vesselId: string,
    content: ApplicationContent,
    note: string | null,
  ): Promise<SubmitOutcome>;
  applications(vesselId?: string): Promise<LibraryApplication[]>;
}

/**
 * Gemide kütüphane talepleri (ADR 0063 §7): ofisin talebi önizlenir, kabul edilir ya da gerekçeyle
 * reddedilir.
 */
export interface LibraryRequestSource {
  list(): Promise<{ applications: LibraryApplication[]; can: { decide: boolean } }>;
  preview(id: string): Promise<ApplicationPlan>;
  accept(id: string): Promise<void>;
  reject(id: string, reason: string): Promise<void>;
}

/** Hatanın planı (`APPLICATION_HAS_ERRORS`). */
export function planOfError(error: unknown): ApplicationPlan | null {
  if (typeof error !== 'object' || error === null) return null;
  const payload = (error as { payload?: unknown }).payload;
  const plan =
    typeof payload === 'object' && payload !== null
      ? (payload as { plan?: unknown }).plan
      : (error as { plan?: unknown }).plan;
  return plan && typeof plan === 'object' ? (plan as ApplicationPlan) : null;
}
